import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetColorIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as DownloadIcon } from '@elementary/common-ui/assets/download.svg';
import { Button, Flex, FlexColumn } from '@elementary/common-ui/components';
import Table, {
  QuickFilter,
  TableFilters,
  TableEmptyState,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import { CATALOG_TABLE_FILTERS } from '@elementary/common-ui/constants/urlParams';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import useSidePanelParams from '@elementary/common-ui/hooks/useSidePanelParams';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import Humanize from 'humanize-plus';
import AssetsTableActionMenu from 'shared/components/AssetsTableActionMenu';
import { EDITABLE_ASSET_TYPES } from 'shared/constants';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import BulkEditWizard from '../BulkEditWizard';
import { BulkEditProperty } from '../BulkEditWizard/types';
import { CatalogFilterOptions } from '../types';
import useCatalogSidePanel from '../../hooks/useCatalogSidePanel';
import { useCatalogTableColumns, useCatalogTableData } from './hooks';
import { CatalogTableAssetData } from './types';

const TableWrapper = styled(FlexColumn)`
  position: relative;
  background-color: ${({ theme }) => theme.palette.common.white};
  overflow: hidden;
  width: 100%;
  height: 100%;
  gap: 24px;
`;

const TableHeader = styled(Flex)`
  gap: 24px;
  padding-bottom: 16px;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

const localStorageKey = 'catalog_table';

const CatalogTable = () => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [bulkEditWizardEditProperty, setBulkEditWizardEditProperty] = useState<
    BulkEditProperty | undefined
  >();
  const [filteredTableData, setFilteredTableData] = useState<
    CatalogTableAssetData[]
  >([]);

  const { treeType } = useTreeViewParams();
  const { openSidePanel, closeSidePanel, selectedId } = useSidePanelParams();
  useCatalogSidePanel();
  const hasPermission = useEnvPermission();
  const canEdit = hasPermission('EDIT_ASSETS');
  const {
    data,
    filterOptions,
    onCSVDownload,
    isExporting,
    isAssetsFetching,
    isFilterOptionsFetching,
    fetchNextPage,
    hasNextPage,
    isTableAssetsFetching,
  } = useCatalogTableData({
    treeType,
    search: debouncedSearch,
  });
  const tableColumns = useCatalogTableColumns({ data, filterOptions });

  const selectedIds = isSelectAll
    ? excludedItems
      ? filteredTableData
          .map((model) => model.id)
          .filter((id) => !excludedItems.includes(id))
      : 'all'
    : selectedItems;

  const selectedModels =
    selectedIds === 'all'
      ? filteredTableData
      : filteredTableData.filter((model) => selectedIds.includes(model.id));

  const chatContext = useMemo(() => {
    if (selectedModels.length === 0) return undefined;
    return {
      assetIds: selectedModels.map((m) => m.id),
      displayLabels: selectedModels.map((m) => m.name),
    };
  }, [selectedModels]);

  useChatContext(chatContext);

  const tableSearchFunc = useCallback(
    (searchVal: string, row: CatalogTableAssetData) => {
      const searchColumns: (keyof CatalogTableAssetData)[] = [
        'name',
        'assetType',
        'description',
        'owners',
        'tags',
        'path',
      ];
      return searchColumns.some((col) =>
        row[col]?.toString().toLowerCase().includes(searchVal.toLowerCase()),
      );
    },
    [],
  );

  const onScrollToBottom = useCallback(() => {
    if (fetchNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const filterCounter = useMemo(() => {
    if (filterOptions) {
      const criticalAssetsFilterOptions = filterOptions['isCritical'];
      const artifactTypeFilterOptions = filterOptions['artifactType'];
      const all = criticalAssetsFilterOptions?.reduce(
        (acc, curr) => acc + (curr.count || 0),
        0,
      );
      const isCriticalCount =
        criticalAssetsFilterOptions?.find((option) => option.value === 'true')
          ?.count || 0;
      const exposureCount =
        artifactTypeFilterOptions?.find((option) => option.value === 'exposure')
          ?.count || 0;
      return {
        [CatalogFilterOptions.All]: all,
        [CatalogFilterOptions.CriticalAssets]: isCriticalCount,
        [CatalogFilterOptions.BIAssets]: exposureCount,
      };
    }
    return data.reduce(
      (acc, curr) => {
        if (curr.isCritical) {
          acc[CatalogFilterOptions.CriticalAssets] += 1;
        }

        if (curr.artifactType === 'exposure') {
          acc[CatalogFilterOptions.BIAssets] += 1;
        }

        return acc;
      },
      {
        [CatalogFilterOptions.All]: data.length,
        [CatalogFilterOptions.CriticalAssets]: 0,
        [CatalogFilterOptions.BIAssets]: 0,
      },
    );
  }, [data, filterOptions]);

  const quickFilters: QuickFilter[] = [
    treeType !== 'bi_assets' && {
      name: isFilterOptionsFetching
        ? 'Exposures'
        : `Exposures (${Humanize.intComma(
            filterCounter[CatalogFilterOptions.BIAssets],
          )})`,
      filters: [
        {
          property: 'artifactType',
          operator: 'equals',
          value: ['exposure'],
        },
      ],
      loading: isFilterOptionsFetching,
    },
    {
      name: isTableAssetsFetching
        ? 'Critical assets'
        : `Critical assets (${Humanize.intComma(
            filterCounter[CatalogFilterOptions.CriticalAssets],
          )})`,
      loading: isTableAssetsFetching,
      icon: <CriticalAssetColorIcon width={20} />,
      filters: [
        {
          property: 'isCritical',
          operator: 'equals',
          value: ['true'],
        },
      ],
    },
  ].filter(Boolean) as QuickFilter[];

  const clearSelections = () => {
    setSelectedItems([]);
    setExcludedItems([]);
    setIsSelectAll(false);
    setBulkEditWizardEditProperty(undefined);
  };

  useEffect(() => {
    if (selectedIds.length && selectedIds !== 'all') {
      const isSelectedIdsInTable = selectedIds.every((id) =>
        filteredTableData.find((tableItem) => tableItem.id === id),
      );

      if (!isSelectedIdsInTable) {
        clearSelections();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, filteredTableData]);

  useViewLoadTracking({
    name: 'Catalog table data',
    loadDeps: [(data?.length && !isTableAssetsFetching) || null],
  });

  const isLoading = useMemo(
    () => isAssetsFetching || isTableAssetsFetching,
    [isAssetsFetching, isTableAssetsFetching],
  );

  return (
    <TableWrapper>
      <TableHeader>
        <Flex gap="24px">
          <TableFilters
            data={data}
            columns={tableColumns}
            urlParamsPrefix={CATALOG_TABLE_FILTERS}
            search={search}
            onSearch={setSearch}
            quickFilters={quickFilters}
            clearFiltersChipLabel={
              isFilterOptionsFetching
                ? 'All'
                : `All (${Humanize.intComma(
                    filterCounter[CatalogFilterOptions.All],
                  )})`
            }
            isClearFiltersLoading={isFilterOptionsFetching}
            disabled={isLoading}
          />
        </Flex>

        <Flex sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onCSVDownload}
            startIcon={<DownloadIcon width={12} />}
            disabled={!data.length || isLoading || isExporting}
          >
            {isExporting ? 'Exporting...' : 'CSV'}
          </Button>
          <ToggleColumnsMenu
            columns={tableColumns}
            localStorageKey={localStorageKey}
          />
        </Flex>
      </TableHeader>

      <Table
        wrapperSx={{
          height: '100%',
          maxHeight: '100%',
        }}
        idKey="id"
        data={data}
        columns={tableColumns}
        onRowClick={({ id }) =>
          id === selectedId ? closeSidePanel() : openSidePanel('asset', id)
        }
        isLoading={isLoading}
        localStorageKey={localStorageKey}
        urlParamsPrefix={CATALOG_TABLE_FILTERS}
        search={search}
        searchFunc={fetchNextPage ? undefined : tableSearchFunc}
        selectedItems={selectedItems}
        isSelectAll={isSelectAll}
        excludedItems={excludedItems}
        onSelectItem={(items) => setSelectedItems(items)}
        onExcludeItem={(items) => setExcludedItems(items)}
        onSelectAllChange={(val) => setIsSelectAll(val)}
        emptyStateRenderer={TableEmptyState}
        onFilteredItemsChange={(items) => setFilteredTableData(items)}
        selectionColumn={canEdit}
        focusedRows={selectedId ? [selectedId] : []}
        onScrollToBottom={onScrollToBottom}
        isControlled={!!fetchNextPage}
        isFilterOptionsLoading={isFilterOptionsFetching}
        bottomScrollPadding={100}
      />

      <AssetsTableActionMenu
        selectedAssets={selectedModels.map((model) => model.id)}
        onOpenWizard={(value) => setBulkEditWizardEditProperty(value)}
        isCriticalAssetsLoading={isTableAssetsFetching}
        isEditable={selectedModels?.every((model) =>
          EDITABLE_ASSET_TYPES.includes(model.assetType),
        )}
        onClose={clearSelections}
      />

      <BulkEditWizard
        isOpen={!!bulkEditWizardEditProperty}
        editProperty={bulkEditWizardEditProperty}
        onClose={() => setBulkEditWizardEditProperty(undefined)}
        onSubmit={clearSelections}
        modelIds={selectedModels.map((m) => m.id)}
      />
    </TableWrapper>
  );
};

export default CatalogTable;
